/**
 * "Copy an existing PDApp's icon" — the list, shared by every surface that
 * offers it (#788).
 *
 * TWO surfaces show it: the editor's Add-PDApp-icon dialog, and the chat card
 * the agent raises. One component, because the interesting part is not the rows
 * — it is what happens when a row turns out to be unreachable, and two copies of
 * that policy would drift.
 *
 * WHY a row can be unreachable at all: the list comes from `/api/workspaces`,
 * which permission-filters with the folder ACCESS SUMMARY, while the export
 * gates on `resolveFilePermission`. Those are different computations over the
 * same question — and the row may also have been deleted, moved or unshared in
 * the seconds since the list was cached (30s `staleTime`). The export is the
 * gate and answers 404 for all of it; the list is a convenience. A row the
 * export refuses is REMOVED rather than left to be clicked again.
 *
 * And the two failures are kept APART. The old single `catch` wrapped the fetch
 * AND the caller's handling of the result, so anything that went wrong
 * downstream was reported as "it may have been moved, deleted, or unshared" —
 * about an app that was right there and perfectly readable. A wrong diagnosis
 * sends someone looking in the wrong place; only the EXPORT can say an app is
 * unreachable, so only its failure says so.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { Loader2, Search } from 'lucide-react'
import { MiniappIcon } from '@papan/core-ui/ui'
// The projects READ, not the drive UI: this component is rendered in the chat
// as well as in the editor, and the barrel would put the whole drive stack there.
import { useProjects, type ProjectItem } from '@papan/files/ui/projects'
import { miniappApi } from '@papan/files/client'
import {
  isMiniappFolder,
  stripPointerSuffix,
  appDisplayName,
  parseMiniappIcon,
  parseMiniappIconRev,
  type MiniappIconSpec,
} from '@papan/types'
import type { PortablePdappMetadata } from '@papan/types/miniapp-icon-svg'
import { errorText } from '@papan/api-client'

export interface PdappAppPickerProps {
  /** The app being edited — left out, because an app linking to itself is never
   *  what the author meant. */
  excludeFolderId?: string | null
  /** The chosen app's PORTABLE metadata, through the one export (gated on THIS
   *  user's read). Answer `false` to keep the picker open — the caller refused
   *  what it was given and the author should be able to try another. */
  onPick: (meta: PortablePdappMetadata, name: string) => Promise<boolean> | boolean
  /** Fills its container; the surfaces differ (a dialog's scroll area, a chat
   *  card's short list). */
  maxHeightClass?: string
  autoFocus?: boolean
}

export function PdappAppPicker({
  excludeFolderId,
  onPick,
  maxHeightClass = 'flex-1',
  autoFocus = true,
}: PdappAppPickerProps) {
  const { t } = useTranslation('collab')
  const projects = useProjects()
  const [filter, setFilter] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Rows the export refused. They are gone for this session of the picker:
   *  leaving one in place invites the same click and the same refusal. */
  const [unreachable, setUnreachable] = useState<ReadonlySet<string>>(new Set())
  const cancelled = useRef(false)
  // LATCHED on unmount. Nothing ever set it, so all four guards below were dead
  // code: closing the dialog mid-export still ran `setUnreachable`/`setError`
  // after unmount, and permanently marked a row unreachable for a picker the
  // author had already dismissed. Re-armed on mount because <StrictMode>
  // mounts, unmounts and remounts in dev.
  useEffect(() => {
    cancelled.current = false
    return () => { cancelled.current = true }
  }, [])

  const apps = useMemo(() => {
    const all = (projects.data ?? []).filter((p: ProjectItem) =>
      // `stripPointerSuffix` is deliberately NOT used: a `.shared` POINTER row is
      // not a pickable target (its id is the pointer in the viewer's own tenant
      // and the export refuses it), so `Foo.pdapp.shared` must not match.
      isMiniappFolder(p.name)
      && p.folderId !== excludeFolderId
      && !p.broken
      && !unreachable.has(p.folderId))
    const q = filter.trim().toLowerCase()
    return { all, shown: (q ? all.filter(p => p.name.toLowerCase().includes(q)) : all).slice(0, 200) }
  }, [projects.data, filter, excludeFolderId, unreachable])

  /**
   * "Where does this one live" — the FULL folder path, the same way the rest of
   * the product shows a location.
   *
   * It was `disambiguateParentLabels` (the shortest parent run unique within the
   * set). That is right for a sidebar and wrong here: the shortest-unique rule
   * gives a DIFFERENT depth per row, so one app reads `PDApps`, its neighbour
   * `dev@demo.local/PDApps` and a third nothing at all — three shapes for one
   * kind of fact, which reads as a bug rather than as information. A full path
   * is the same shape on every row, and it is what the breadcrumb and the drive
   * already show.
   *
   * Rendered with `›` for the same reason the breadcrumb does, truncated in the
   * row, and the untruncated path is on the element's `title`.
   *
   * An app at the TOP of the drive has no parent to name, and an empty second
   * line is the inconsistency this was meant to remove: eleven rows carrying a
   * location and one carrying nothing reads as a missing value, not as "it
   * lives at the top". So that case gets said out loud. EVERY row has the line.
   *
   * The root's word comes from i18n and not the literal the other pickers use
   * ('papan'): this instance's drive is not necessarily called that. Papan
   * ships one deployment per client — see docs/WHITELABEL.md — and a brand
   * word baked into a user-reaching string is the thing that rule exists to
   * stop. The three existing literals are older than the rule; this is not a
   * fourth.
   *
   * NOTE: these ancestor names come from `/api/workspaces`, which
   * permission-filters the LEAF and not the folders above it. Filtering
   * breadcrumbs by viewer permission belongs at that endpoint, where every
   * consumer inherits it.
   */
  const places = useMemo(() => {
    const rows = (projects.data ?? []).filter((p: ProjectItem) =>
      isMiniappFolder(p.name) && p.folderId !== excludeFolderId && !p.broken && !unreachable.has(p.folderId))
    return new Map(rows.map((p) => {
      const segs = (p.path ?? '').split('/').filter(Boolean)
      // Drop the leaf: we say where it LIVES, not repeat its own name.
      const parents = segs.slice(0, -1)
      return [p.folderId, parents.length ? parents.join(' › ') : t('pdappPicker.atRoot')]
    }))
  }, [projects.data, excludeFolderId, unreachable, t])

  const pick = async (p: ProjectItem) => {
    setBusyId(p.folderId)
    setError(null)
    const name = appDisplayName(stripPointerSuffix(p.name))
    let meta: PortablePdappMetadata
    try {
      meta = await miniappApi.getMetadata(p.folderId)
    } catch {
      if (cancelled.current) return
      // ONLY the export can say this. It answers 404 for "cannot read" and "not
      // a PDApp" alike, so the wording covers both without inventing a reason —
      // and the row goes, because clicking it again cannot end differently.
      setUnreachable((prev) => new Set(prev).add(p.folderId))
      setError(t('pdappPicker.unreachable', { name }))
      setBusyId(null)
      return
    }
    if (cancelled.current) return
    try {
      const taken = await onPick(meta, name)
      if (cancelled.current) return
      if (!taken) setBusyId(null)
    } catch (e) {
      // The app was read fine; something downstream refused it. Say that, and
      // leave the row alone — it is not the one with the problem.
      if (cancelled.current) return
      const text = errorText(e, t('pdappPicker.failed', { name }))   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
      setBusyId(null)
    }
  }

  return (
    <>
      <div className="px-4 py-2.5 border-b themed-border">
        <div className="flex items-center gap-2 px-2 py-1.5 rounded themed-bg-tertiary border themed-border">
          <Search size={13} className="themed-text-muted shrink-0" />
          <input
            autoFocus={autoFocus}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('pdappPicker.search')}
            className="flex-1 bg-transparent text-sm themed-text outline-none"
          />
        </div>
      </div>

      <div className={`${maxHeightClass} overflow-y-auto px-2 py-2`} data-testid="add-icon-list">
        {projects.isLoading ? (
          <div className="flex items-center gap-2 px-2 py-6 text-sm themed-text-muted">
            <Loader2 size={14} className="animate-spin" /> {t('pdappPicker.loading')}
          </div>
        ) : apps.shown.length === 0 ? (
          <div className="px-2 py-6 text-sm themed-text-muted">
            {filter ? t('pdappPicker.noMatch') : t('pdappPicker.empty')}
          </div>
        ) : apps.shown.map((p) => {
          const name = appDisplayName(stripPointerSuffix(p.name))
          const spec: MiniappIconSpec | null = parseMiniappIcon(p.metadataJson ?? null)
          return (
            <button
              key={p.folderId}
              type="button"
              disabled={busyId !== null}
              onClick={() => { cancelled.current = false; void pick(p) }}
              data-app-id={p.folderId}
              className="w-full flex items-center gap-3 px-2 py-2 min-h-[44px] rounded text-left hover:themed-bg-tertiary disabled:opacity-50"
            >
              <span className="shrink-0 w-7 h-7 flex items-center justify-center">
                {busyId === p.folderId
                  ? <Loader2 size={14} className="animate-spin themed-text-muted" />
                  : <MiniappIcon spec={spec} name={name} size={26} refId={p.folderId} rev={parseMiniappIconRev(p.metadataJson ?? null)} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm themed-text truncate">{name}</span>
                {/* Secondary line, muted and smaller: it answers "which one is
                    this" at a glance without competing with the name.
                    UNCONDITIONAL — a row that drops it is a row whose location
                    looks unknown rather than shallow. */}
                <span
                  className="block text-[11px] themed-text-muted truncate"
                  title={places.get(p.folderId)}
                  data-testid={`add-icon-place-${p.folderId}`}
                >
                  {places.get(p.folderId)}
                </span>
              </span>
            </button>
          )
        })}
      </div>

      {error && (
        <div className="px-4 py-2 text-xs text-red-400 border-t themed-border" data-testid="add-icon-error">{error}</div>
      )}
    </>
  )
}
